/* eslint-disable */
import React, { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";

export default function DropdownCell({
  id,
  patient,
  doctors,
  selectedDropdownId,
  toggleDropdown,
  handleAssignDoctor,
}) {
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const [pos, setPos] = useState({
    top: undefined,
    left: undefined,
    maxHeight: undefined,
  });

  // Measure dropdown size reliably (while it's rendered). Works when it's in the DOM.
  function measureDropdownSize() {
    const dd = dropdownRef.current;
    if (!dd) return { w: 0, h: 0 };
    // ensure visible for measurement
    const prev = {
      display: dd.style.display,
      visibility: dd.style.visibility,
      maxHeight: dd.style.maxHeight,
      overflowY: dd.style.overflowY,
    };
    dd.style.display = "block";
    dd.style.visibility = "hidden";
    dd.style.maxHeight = "";
    dd.style.overflowY = "";
    const w = dd.offsetWidth;
    const h = dd.offsetHeight;
    Object.assign(dd.style, prev);
    return { w, h };
  }

  function clampLeft(left, dropdownWidth, margin = 8) {
    const vw = document.documentElement.clientWidth;
    const minLeft = margin;
    const maxLeft = vw - dropdownWidth - margin;
    return Math.max(minLeft, Math.min(left, maxLeft));
  }

  // position calculation using viewport coords + fixed positioning
  function computeAndSetPosition() {
    const trigger = triggerRef.current;
    const dd = dropdownRef.current;
    if (!trigger || !dd) return;

    const rect = trigger.getBoundingClientRect(); // viewport coords

    // measure after render; ensure measurement happens after paint
    const { w: ddW, h: ddH } = measureDropdownSize();

    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const margin = 6;

    let top;
    let maxHeight;

    if (ddH + margin <= spaceBelow) {
      // fits below
      top = rect.bottom + margin; // viewport coord (fixed uses viewport)
    } else if (ddH + margin <= spaceAbove) {
      // fits above
      top = rect.top - ddH - margin;
    } else {
      // doesn't fit fully either side -> pick side with more space and clamp
      if (spaceBelow >= spaceAbove) {
        top = rect.bottom + margin;
        maxHeight = Math.max(spaceBelow - margin, 80);
      } else {
        // place above
        // we still position top as (rect.top - ddH - margin) so dropdown's bottom aligns with rect.top
        top = rect.top - ddH - margin;
        maxHeight = Math.max(spaceAbove - margin, 80);
      }

      // If we decided above but top is negative, clamp to margin
      if (top < margin) top = margin;
    }

    // left: align to left edge of trigger by default (can modify to center)
    let left = rect.left;
    left = clampLeft(left, ddW, 8);

    setPos({ top, left, maxHeight });
  }

  // Recompute position when dropdown opens and on resize/scroll
  useEffect(() => {
    if (selectedDropdownId === id) {
      // Use rAF to allow the DOM to settle and CSS to apply before measuring
      const raf = requestAnimationFrame(() => {
        computeAndSetPosition();
      });

      const onScrollOrResize = () => {
        computeAndSetPosition();
      };
      window.addEventListener("scroll", onScrollOrResize, { passive: true });
      window.addEventListener("resize", onScrollOrResize);

      // click outside => close (preserves your toggle semantics)
      const onDocClick = (e) => {
        const dd = dropdownRef.current;
        const trigger = triggerRef.current;
        if (!dd || !trigger) return;
        if (!dd.contains(e.target) && !trigger.contains(e.target)) {
          if (selectedDropdownId === id) toggleDropdown(id);
        }
      };
      document.addEventListener("click", onDocClick);

      return () => {
        cancelAnimationFrame(raf);
        window.removeEventListener("scroll", onScrollOrResize);
        window.removeEventListener("resize", onScrollOrResize);
        document.removeEventListener("click", onDocClick);
      };
    } else {
      // closed — clear pos
      setPos({ top: undefined, left: undefined, maxHeight: undefined });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDropdownId, id]);

  // If user moves mouse out of dropdown, close it (optional; matches earlier behavior)
  useEffect(() => {
    const dd = dropdownRef.current;
    if (!dd) return;
    const onMouseLeave = () => {
      if (selectedDropdownId === id) toggleDropdown(id);
    };
    dd.addEventListener("mouseleave", onMouseLeave);
    return () => dd.removeEventListener("mouseleave", onMouseLeave);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDropdownId, id]);

  // Helper function to check if a doctor is valid
  const isValidDoctor = (doctor) => {
    if (!doctor) return false;
    
    if (typeof doctor === "string") {
      const cleanName = doctor.trim().toLowerCase();
      return cleanName !== "" && 
             cleanName !== "doctor common" && 
             cleanName !== "doctorcommon" &&
             cleanName !== "null" &&
             cleanName !== "undefined";
    }
    
    if (typeof doctor === "object" && doctor.name) {
      const cleanName = doctor.name.trim().toLowerCase();
      return cleanName !== "" && 
             cleanName !== "doctor common" && 
             cleanName !== "doctorcommon" &&
             cleanName !== "null" &&
             cleanName !== "undefined";
    }
    
    return false;
  };

  // Get the display name for the dropdown trigger
  const getDisplayName = () => {
    if (!patient.DoctorAssigned || patient.DoctorAssigned.length === 0) {
      return "Select Doctor";
    }

    // Filter out invalid doctors
    const validDoctors = patient.DoctorAssigned.filter(isValidDoctor);

    if (validDoctors.length === 0) {
      return "Select Doctor";
    }

    // Find the latest assigned doctor (most recent assignedAt/timestamp)
    const latestDoctor = validDoctors
      .sort((a, b) => {
        // Handle both string and object formats
        if (typeof a === "string" && typeof b === "string") return 0;
        if (typeof a === "string") return -1; // String comes first (legacy)
        if (typeof b === "string") return 1;

        // Both are objects, sort by timestamp
        const dateA = new Date(a.assignedAt || a.timestamp || 0);
        const dateB = new Date(b.assignedAt || b.timestamp || 0);
        return dateB - dateA; // Sort descending (latest first)
      })[0];

    if (latestDoctor) {
      const doctorName = typeof latestDoctor === "string" ? latestDoctor : latestDoctor.name;
      return doctorName;
    }

    return "Select Doctor";
  };

  // Check if patient has valid assigned doctors for CSS class
  const hasValidAssignedDoctors = () => {
    if (!patient.DoctorAssigned || patient.DoctorAssigned.length === 0) {
      return false;
    }
    return patient.DoctorAssigned.some(isValidDoctor);
  };

  //deepa fix ui - Wrap in <td> to properly apply table cell styling
  return (
    <td>
      <div style={{ position: "static" }}>
        <div
          ref={triggerRef}
          className={`doctor-display ${hasValidAssignedDoctors() ? "assigned" : ""}`}
          onClick={() => toggleDropdown(id)}
        >
          <span>{getDisplayName()}</span>
          <ChevronDown size={16} strokeWidth={0.5} className="chev" />
        </div>

      {selectedDropdownId === id && (
        <div
          ref={dropdownRef}
          className="custom-dropdown show-dropdown"
          role="menu"
          aria-hidden="false"
          style={{
            position: "fixed",
            top: pos.top !== undefined ? `${pos.top}px` : undefined,
            left: pos.left !== undefined ? `${pos.left}px` : undefined,
            maxHeight: pos.maxHeight !== undefined ? `${pos.maxHeight}px` : undefined,
            overflowY: pos.maxHeight !== undefined ? "auto" : undefined,
            zIndex: 10000,
            width: 220,
          }}
        >
          <div className="dropdown-label">Assigned Doctors</div>

          {(() => {
            const hasAssignedHOD =
              patient.DoctorAssigned &&
              patient.DoctorAssigned.some((d) => {
                if (typeof d === "string") return d === "HOD";
                return d.name === "HOD";
              });

            const assignedOptions = [];
            let hodShown = false;

            if (hasAssignedHOD && !hodShown) {
              assignedOptions.push(
                <div
                  key="assigned-hod"
                  className="dropdown-option"
                  onClick={() => handleAssignDoctor(id, "HOD", true, false)}
                >
                  <span className="doctor-option-name">HOD</span>
                  <span className="doctor-option-icon">☑</span>
                </div>
              );
              hodShown = true;
            }

            Object.keys(doctors).forEach((doctorKey, idx) => {
              const doctorName = doctors[doctorKey].Name;
              const doctorRole = doctors[doctorKey].Role;
              const isHOD = doctorRole && doctorRole.toLowerCase().includes("hod");
              const isDoctorCommon = doctorRole && doctorRole.toLowerCase().includes("doctorcommon");
              
              if (isDoctorCommon || isHOD) return;
              
              const isAssigned =
                patient.DoctorAssigned &&
                patient.DoctorAssigned.some((d) =>
                  typeof d === "string" ? d === doctorName : d.name === doctorName
                );
              
              if (isAssigned) {
                assignedOptions.push(
                  <div
                    key={`assigned-${idx}`}
                    className="dropdown-option"
                    onClick={() => handleAssignDoctor(id, doctorName, true, false)}
                  >
                    <span className="doctor-option-name">{doctorName}</span>
                    <span className="doctor-option-icon">☑</span>
                  </div>
                );
              }
            });

            return assignedOptions;
          })()}

          <div className="dropdown-label">Unassigned Doctors</div>

          {(() => {
            const hasAssignedHOD =
              patient.DoctorAssigned &&
              patient.DoctorAssigned.some((d) =>
                typeof d === "string" ? d === "HOD" : d.name === "HOD"
              );
            const hasHODDoctors = Object.keys(doctors).some((doctorKey) => {
              const dr = doctors[doctorKey].Role;
              return dr && dr.toLowerCase().includes("hod");
            });
            const unassignedOptions = [];
            let hodShown = false;

            if (hasHODDoctors && !hasAssignedHOD && !hodShown) {
              unassignedOptions.push(
                <div
                  key="unassigned-hod"
                  className="dropdown-option"
                  onClick={() => handleAssignDoctor(id, "HOD", false, false)}
                >
                  <span className="doctor-option-name">HOD</span>
                  <span className="doctor-option-icon">☐</span>
                </div>
              );
              hodShown = true;
            }

            Object.keys(doctors).forEach((doctorKey, idx) => {
              const doctorName = doctors[doctorKey].Name;
              const doctorRole = doctors[doctorKey].Role;
              const isHOD = doctorRole && doctorRole.toLowerCase().includes("hod");
              const isDoctorCommon = doctorRole && doctorRole.toLowerCase().includes("doctorcommon");
              
              if (isDoctorCommon || isHOD) return;
              
              const isAssigned =
                patient.DoctorAssigned &&
                patient.DoctorAssigned.some((d) =>
                  typeof d === "string" ? d === doctorName : d.name === doctorName
                );
              
              if (!isAssigned) {
                unassignedOptions.push(
                  <div
                    key={`unassigned-${idx}`}
                    className="dropdown-option"
                    onClick={() => handleAssignDoctor(id, doctorName, false, false)}
                  >
                    <span className="doctor-option-name">{doctorName}</span>
                    <span className="doctor-option-icon">☐</span>
                  </div>
                );
              }
            });

            return unassignedOptions;
          })()}
        </div>
      )}
      </div>
    </td>
  );
}